/**
 * Visual component.
 *
 * All WordPress images should run through this component.
 *
 * Expects an image prop that is a WordPress Image object.
 */
import get from "lodash.get";
import { createComponent } from "react-fela";
import React from "react";
import { replaceWPSiteWithFrontend } from "~/modules/link-parser";

// Build a responsive image source set from WordPress media sizes.
const SRC_KEYS = [
    { size: "thumbnail", breakpoint: 75 },
    { size: "medium", breakpoint: 150 },
    { size: "medium_large", breakpoint: 382 },
    { size: "large", breakpoint: 512 },
    { size: "xlarge", breakpoint: 768 },
    { size: "xxlarge", breakpoint: 1024 }
];

const promoteVisualRootStyles = styles => {
    if (!styles || typeof styles !== "object") return styles;

    if (Array.isArray(styles)) return styles.map(promoteVisualRootStyles);

    return Object.keys(styles).reduce((promoted, key) => {
        const value = promoteVisualRootStyles(styles[key]);

        if (key === "& .vv-visual" || key === "> .vv-visual") {
            return {
                ...promoted,
                ...value
            };
        }

        if (key === "& .vv-align-middle") {
            return {
                ...promoted,
                ...value
            };
        }

        promoted[key.replace("& .vv-visual ", "& ")] = value;

        return promoted;
    }, {});
};

export default class Visual extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            imageLoaded: false,
            videoLoaded: false,
            shouldLoadVideo: !props.deferVideoOnMobile
        };
    }

    componentDidMount() {
        if (this.image && this.image.complete) {
            this.setState({ imageLoaded: true });
        }

        if (this.props.deferVideoOnMobile) {
            this.deferMobileVideo();
        }
    }

    componentWillUnmount() {
        if (this.loadVideoTimeout) {
            clearTimeout(this.loadVideoTimeout);
        }

        if (this.removeVideoLoadListener) {
            this.removeVideoLoadListener();
        }
    }

    deferMobileVideo() {
        if (typeof window === "undefined") return;

        const isMobile =
            window.matchMedia &&
            window.matchMedia("(max-width: 767px)").matches;

        if (!isMobile) {
            this.setState({ shouldLoadVideo: true });
            return;
        }

        const loadVideo = () => {
            const schedule =
                window.requestIdleCallback ||
                (callback => {
                    this.loadVideoTimeout = setTimeout(callback, 1);
                });

            schedule(() => this.setState({ shouldLoadVideo: true }));
        };

        const interactionEvents = ["pointerdown", "touchstart", "keydown", "scroll"];
        const onInteraction = () => {
            if (this.removeVideoLoadListener) {
                this.removeVideoLoadListener();
                this.removeVideoLoadListener = null;
            }

            loadVideo();
        };

        interactionEvents.forEach(eventName => {
            window.addEventListener(eventName, onInteraction, {
                passive: true,
                once: true
            });
        });
        this.removeVideoLoadListener = () => {
            interactionEvents.forEach(eventName => {
                window.removeEventListener(eventName, onInteraction);
            });
        };
    }

    get alt() {
        // Supports overriding alt tags.
        if (this.props.alt) return this.props.alt;

        if (!this.props.image || typeof this.props.image === "string") return "";

        // Otherwise, try to get the detail from the image object.
        let { alt, title, description } = this.props.image;
        alt = get(alt, "rendered", alt);
        title = get(title, "rendered", title);
        description = get(description, "rendered", description);

        return alt || title || description || "";
    }

    get aspect() {
        const { image } = this.props;

        if (!image || typeof image === "string") return null;

        const width = get(image, "media_details.width", get(image, "width"));
        const height = get(image, "media_details.height", get(image, "height"));

        if (!width || !height) return null;

        return width / height;
    }

    scrubSource(source) {
        if (!source) return source;

        if (process.env.ENVIRONMENT === "development") {
            if (source.startsWith("/wp-content/")) {
                return `${process.env.WORDPRESS_URL}${source}`;
            }
            if (source.startsWith(process.env.SITE_URL) && source.includes("/wp-content/")) {
                return source.replace(process.env.SITE_URL, process.env.WORDPRESS_URL);
            }
            return source;
        }

        return replaceWPSiteWithFrontend(source);
    }

    get imageSources() {
        const { image } = this.props;

        if (!image) return null;

        if (typeof image === "string") {
            return {
                src: this.scrubSource(image),
                srcSet: null
            };
        }

        const fallback = image.source_url || image.url;
        const sources = SRC_KEYS.map(config => {
            const path = Object.prototype.hasOwnProperty.call(
                image,
                "media_details"
            )
                ? `media_details.sizes.${config.size}.source_url`
                : `sizes.${config.size}`;

            return {
                breakpoint: config.breakpoint,
                source: this.scrubSource(get(image, path, fallback))
            };
        }).filter(source => source.source);

        return {
            src: this.scrubSource(fallback) || get(sources, "0.source"),
            srcSet: sources
                .map(source => `${source.source} ${source.breakpoint}w`)
                .join(", ")
        };
    }

    get visualAspect() {
        return this.props.naturalAspect ? this.aspect : this.props.aspect;
    }

    get videoSources() {
        const { video } = this.props;

        if (!video) return [];

        return (Array.isArray(video) ? video : [video])
            .map(source => this.scrubSource(source))
            .filter(Boolean);
    }

    render() {
        const sources = this.imageSources;
        const {
            align = "left middle",
            autoplay,
            background,
            controls,
            extend,
            fill,
            height,
            isLcpCandidate,
            load,
            loop,
            muted,
            playsinline,
            deferVideoOnMobile,
            width
        } = this.props;
        const aspect = this.visualAspect;
        const shouldFill = fill || aspect || background;
        const hasVideo = this.videoSources.length > 0;
        const activeVideoSources = this.state.shouldLoadVideo
            ? this.videoSources
            : [];
        const posterAspect = hasVideo ? this.aspect : null;
        const layoutAspect = aspect || posterAspect;
        const visualClasses = [
            "vv-visual",
            layoutAspect || background ? "vv-block" : "",
            this.state.imageLoaded ? "vv-image-loaded" : "",
            this.state.videoLoaded ? "vv-video-loaded" : "",
            align.includes("left") ? "vv-align-left" : "",
            align.includes("center") ? "vv-align-center" : "",
            align.includes("right") ? "vv-align-right" : ""
        ]
            .filter(Boolean)
            .join(" ");
        const assetClasses = [
            "vv-asset",
            "vv-image",
            shouldFill || posterAspect ? "vv-fill-asset" : "",
            width ? "vv-has-width" : "",
            height ? "vv-has-height" : "",
            background ? `vv-background-${background}` : ""
        ]
            .filter(Boolean)
            .join(" ");

        if (!sources || !sources.src) return null;

        return (
            <VisualWrapper
                className={visualClasses}
                extend={promoteVisualRootStyles(extend)}
                style={{
                    aspectRatio: layoutAspect ? `${layoutAspect}` : undefined,
                    width,
                    height
                }}
            >
                <img
                    alt={this.alt}
                    className={assetClasses}
                    fetchPriority={isLcpCandidate ? "high" : undefined}
                    loading={isLcpCandidate || load === true ? "eager" : "lazy"}
                    onLoad={() => this.setState({ imageLoaded: true })}
                    ref={image => {
                        this.image = image;
                    }}
                    sizes="100vw"
                    src={sources.src}
                    srcSet={sources.srcSet || undefined}
                    style={
                        background || posterAspect
                            ? {
                                  objectFit: background || "contain",
                                  objectPosition: "center center"
                              }
                            : undefined
                    }
                />
                {hasVideo && (
                    <video
                        autoPlay={this.state.shouldLoadVideo && autoplay}
                        className={`vv-asset vv-video ${
                            shouldFill || hasVideo ? "vv-fill-asset" : ""
                        }`}
                        controls={controls}
                        loop={loop}
                        muted={muted}
                        onCanPlay={() => this.setState({ videoLoaded: true })}
                        playsInline={playsinline}
                        preload={
                            deferVideoOnMobile && !this.state.shouldLoadVideo
                                ? "none"
                                : load === true
                                ? "auto"
                                : "metadata"
                        }
                        style={{
                            objectFit: "contain",
                            objectPosition: "center center",
                            top: 0
                        }}
                    >
                        {activeVideoSources.map(source => (
                            <source src={source} key={source} />
                        ))}
                    </video>
                )}
            </VisualWrapper>
        );
    }
}

const VisualWrapper = createComponent(({ extend }) => ({ extend }), "div");
